import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';

// Import actual school images
import pic01 from '../../assets/gallery/pic01.jpg';
import pic04 from '../../assets/gallery/pic04.jpg';
import pic05 from '../../assets/gallery/pic05.jpg';
import pic06 from '../../assets/gallery/pic06.jpg';
import pic07 from '../../assets/gallery/pic07.jpg';
import pic1 from '../../assets/gallery/pic1.jpg';
import pic2 from '../../assets/gallery/pic2.jpg';
import pic3 from '../../assets/gallery/pic3.jpg';
import pic4 from '../../assets/gallery/pic4.jpg';
import pic15 from '../../assets/gallery/pic15.JPG';
import pic17 from '../../assets/gallery/pic17.JPG';
import pic18 from '../../assets/gallery/pic18.JPG';
import pic22 from '../../assets/gallery/pic22.JPG';
import pic23 from '../../assets/gallery/pic23.JPG';
import pic24 from '../../assets/gallery/pic24.JPG';
import pic25 from '../../assets/gallery/pic25.JPG';
import pic26 from '../../assets/gallery/pic26.JPG';

const Gallery = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  const filters = [
    { id: 'all', label: 'All' },
    { id: 'campus', label: 'Campus' },
    { id: 'activities', label: 'Activities' },
    { id: 'events', label: 'Events' },
  ];

  const images = [
    { id: 1, src: pic01, category: 'campus', alt: 'Stirret Schools Campus' },
    { id: 2, src: pic04, category: 'activities', alt: 'School Activities' },
    { id: 3, src: pic05, category: 'events', alt: 'School Events' },
    { id: 4, src: pic06, category: 'campus', alt: 'Campus Life' },
    { id: 5, src: pic07, category: 'activities', alt: 'Student Activities' },
    { id: 6, src: pic1, category: 'campus', alt: 'Stirret Schools' },
    { id: 7, src: pic2, category: 'activities', alt: 'Learning Environment' },
    { id: 8, src: pic3, category: 'events', alt: 'School Events' },
    { id: 9, src: pic4, category: 'campus', alt: 'Campus View' },
    { id: 10, src: pic15, category: 'activities', alt: 'Classroom Activities' },
    { id: 11, src: pic17, category: 'activities', alt: 'Learning Activities' },
    { id: 12, src: pic18, category: 'campus', alt: 'School Facilities' },
    { id: 13, src: pic22, category: 'events', alt: 'School Celebrations' },
    { id: 14, src: pic23, category: 'activities', alt: 'Student Engagement' },
    { id: 15, src: pic24, category: 'campus', alt: 'School Building' },
    { id: 16, src: pic25, category: 'events', alt: 'Special Events' },
    { id: 17, src: pic26, category: 'activities', alt: 'School Life' },
  ];

  const filteredImages = activeFilter === 'all'
    ? images
    : images.filter(img => img.category === activeFilter);

  return (
    <>
      <PageHeader
        title="Gallery"
        subtitle="Explore life at Stirret Schools through our photo gallery"
        breadcrumbs={[{ label: 'Gallery' }]}
      />

      <section className="section-padding bg-white">
        <div className="container-custom">
          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 md:gap-3 mb-8 md:mb-12">
            {filters.map((filter) => (
              <button
                key={filter.id}
                onClick={() => setActiveFilter(filter.id)}
                className={`px-4 md:px-5 py-2 rounded-full text-sm md:text-base font-medium transition-colors ${
                  activeFilter === filter.id
                    ? 'bg-primary-600 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {/* Gallery Grid */}
          <motion.div layout className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            <AnimatePresence>
              {filteredImages.map((image) => (
                <motion.div
                  key={image.id}
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  className="relative aspect-square overflow-hidden rounded-xl cursor-pointer group"
                  onClick={() => setSelectedImage(image)}
                >
                  <img
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors" />
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      {/* Lightbox */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
          >
            <button
              className="absolute top-4 right-4 text-white/80 hover:text-white p-2"
              onClick={() => setSelectedImage(null)}
            >
              <X size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Gallery;
